import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TokenService } from '../token/token.service';
import { jwtDecode } from 'jwt-decode';

/**
 * Serwis zarządzający danymi zalogowanego użytkownika
 * Dekoduje JWT i udostępnia Observable'e dla uuid, nickname, permissions
 */

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {
  private readonly uuidSubject = new BehaviorSubject<string | null>(null);
  private readonly nicknameSubject = new BehaviorSubject<string | null>(null);
  private readonly permissionsSubject = new BehaviorSubject<string[]>([]);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  private readonly destroy$ = new Subject<void>();

  /** Observable dla UUID użytkownika */
  readonly uuid$ = this.uuidSubject.asObservable();
  /** Observable dla nicku użytkownika */
  readonly nickname$ = this.nicknameSubject.asObservable();
  /** Observable dla uprawnień użytkownika */
  readonly permissions$ = this.permissionsSubject.asObservable();
  /** Observable dla błędów dekodowania tokenu */
  readonly error$ = this.errorSubject.asObservable();

  constructor(private tokenService: TokenService) {
    // Słuchaj zmian statusu autentykacji i ładuj dane użytkownika
    this.tokenService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAuth => {
        if (isAuth) {
          this.loadFromToken();
        } else {
          this.clear();
        }
      });
  }

  /**
   * Czyści zasoby podczas destrukcji
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Dekoduje JWT i ładuje dane użytkownika do observable'i
   * @private
   */
  private loadFromToken(): void {
    const token = this.tokenService.getAccessToken();
    if (!token) {
      this.clear();
      return;
    }

    try {
      const decoded = jwtDecode<any>(token);
      
      // Waliduj strukturę JWT
      this.validateJwtClaims(decoded);
      
      // Wyciągnij UUID
      const uuid = decoded.uuidName?.uuid || decoded.sub || null;
      if (!uuid) {
        throw new Error('JWT missing required claims: uuidName.uuid or sub');
      }
      this.uuidSubject.next(uuid);

      // Wyciągnij nickname
      this.nicknameSubject.next(decoded.uuidName?.name || null);

      // Wyciągnij uprawnienia
      this.permissionsSubject.next(decoded.permissions || []);
      
      // Wyczyść błędy
      this.errorSubject.next(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to decode JWT';
      console.error('UserService: JWT decoding failed:', errorMessage);
      this.errorSubject.next(errorMessage);
      this.clear();
    }
  }

  /**
   * Waliduje strukturę JWT - sprawdza czy zawiera wymagane pola
   * @param decoded - Decoded JWT payload
   * @throws Error jeśli UUID claims brakuje
   * @private
   */
  private validateJwtClaims(decoded: any): void {
    if (!decoded) {
      throw new Error('Decoded JWT is null or undefined');
    }
    
    // UUID musi być w uuidName lub sub
    const hasValidUuid = decoded.uuidName?.uuid || decoded.sub;
    if (!hasValidUuid) {
      console.warn('JWT missing UUID claims: uuidName.uuid or sub');
    }

    // Permissions powinni być array
    if (decoded.permissions && !Array.isArray(decoded.permissions)) {
      console.warn('JWT permissions claim is not an array');
    }
  }

  /**
   * Czyści dane użytkownika - resetuje wszystkie observable'e
   */
  clear(): void {
    this.uuidSubject.next(null);
    this.nicknameSubject.next(null);
    this.permissionsSubject.next([]);
    this.errorSubject.next(null);
  }

  /**
   * Pobiera UUID użytkownika synchronicznie (ze snapshot'u)
   * UWAGA: Preferuj uuid$ observable zamiast tego gettera!
   * @deprecated Używaj uuid$ observable
   * @returns UUID lub null
   */
  get uuid(): string | null {
    return this.uuidSubject.getValue();
  }

  /**
   * Pobiera nickname użytkownika synchronicznie (ze snapshot'u)
   * UWAGA: Preferuj nickname$ observable zamiast tego gettera!
   * @deprecated Używaj nickname$ observable
   * @returns Nickname lub null
   */
  get nickname(): string | null {
    return this.nicknameSubject.getValue();
  }

  /**
   * Pobiera uprawnienia użytkownika synchronicznie (ze snapshot'u)
   * UWAGA: Preferuj permissions$ observable zamiast tego gettera!
   * @deprecated Używaj permissions$ observable
   * @returns Lista uprawnień
   */
  get permissions(): string[] {
    return this.permissionsSubject.getValue();
  }
}
